/**
 * Spoken problems and spoken answers. Pure text-in, text-out: the browser does the actual
 * speaking and listening (web/src/lib/voice.ts); the server uses voiceAllowance for timing.
 */

const UNITS: Record<string, string> = {
  "km/h": "kilometers per hour", mph: "miles per hour", km: "kilometers", mi: "miles", kg: "kilograms", lb: "pounds",
  "°C": "degrees Celsius", "°F": "degrees Fahrenheit", in: "inches", cm: "centimeters", m: "meters", ft: "feet",
  L: "liters", gal: "gallons", oz: "ounces", g: "grams",
};

/** A problem's question and note, rewritten so a speech engine reads it the way a person would say it. */
export function spokenText(q: string, note = ""): string {
  let s = q;
  const conv = /^(-?[\d,.]+) (\S+) → (\S+)$/.exec(s);
  if (conv) s = `${conv[1]} ${UNITS[conv[2]] ?? conv[2]} to ${UNITS[conv[3]] ?? conv[3]}`;
  s = s.replace(/(\d[\d,]*(?:\.\d+)?)²/g, "$1 squared").replace(/√\s*/g, "square root of ")
    .replace(/×/g, " times ").replace(/÷/g, " divided by ").replace(/[−–]/g, " minus ").replace(/\+/g, " plus ")
    .replace(/→/g, " to ").replace(/%/g, " percent").replace(/\(/g, " open parenthesis ").replace(/\)/g, " close parenthesis ");
  const n = note.replace(/±/g, "plus or minus ").replace(/[−–]/g, "minus").replace(/%/g, " percent").replace(/°/g, " degrees ");
  return (s + (n ? ". " + n : "")).replace(/\s+/g, " ").replace(/ \./g, ".").trim();
}

/** Extra seconds a voice player gets on a problem: time to hear it read, plus speaking the answer. */
export function voiceAllowance(q: string, note = ""): number {
  const words = spokenText(q, note).split(" ");
  let secs = 0;
  for (const w of words) secs += /\d/.test(w) ? 0.3 + 0.12 * w.replace(/\D/g, "").length : 0.35;
  return Math.min(15, Math.round((1.5 + secs) * 2) / 2);
}

const ONES: Record<string, number> = {
  zero: 0, oh: 0, o: 0, one: 1, won: 1, two: 2, to: 2, too: 2, three: 3, four: 4, for: 4, five: 5, six: 6, seven: 7, eight: 8, ate: 8,
  nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
};
const TENS: Record<string, number> = { twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
const SCALES: Record<string, number> = { thousand: 1e3, million: 1e6 };
const DIGIT_WORDS = new Set(["zero", "oh", "o", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"]);

export type Heard =
  | { kind: "number"; value: string }
  | { kind: "command"; cmd: "repeat" | "hint" | "skip" | "next" | "cancel" };

const COMMANDS: [RegExp, Extract<Heard, { kind: "command" }>["cmd"]][] = [
  [/^(repeat( that| it)?|say (that )?again|again|what|come again|one more time)$/, "repeat"],
  [/^(hint|help|give me a hint|i need a hint)$/, "hint"],
  [/^(skip|pass|i (do not|don't) know|no idea)$/, "skip"],
  [/^(next|next one|continue|go on|okay|ok)$/, "next"],
  [/^(no|nope|cancel|clear|wrong|wait|hold on|scratch that|never mind|nevermind|undo|oops)$/, "cancel"],
];

/**
 * What a speech recognizer heard, as a command or a number string the normal answer check accepts.
 * Recognizers usually return digits already ("47", "1,200"); spelled-out numbers are handled too.
 * Returns null when nothing usable was heard.
 */
export function interpretSpeech(raw: string): Heard | null {
  const t = raw.toLowerCase().replace(/[.!?]+$/g, "").replace(/\s+/g, " ").trim();
  if (!t) return null;
  for (const [re, cmd] of COMMANDS) if (re.test(t)) return { kind: "command", cmd };
  const neg = /(^|\s)(minus|negative)\s/.test(t + " ") || /(^|\s)-\s?\d/.test(t);
  const digits = t.replace(/(\d)[ ,](?=\d{3}\b)/g, "$1").replace(/,/g, "");
  const m = /\d+(\.\d+)?|\.\d+/.exec(digits);
  if (m) return { kind: "number", value: (neg ? "-" : "") + m[0] };

  const toks = t.replace(/[-,]/g, " ").split(" ").filter((w) => w && w !== "and");
  const only = toks.every((w) => w in ONES || w in TENS || w in SCALES || w === "hundred" || w === "point" || w === "minus" || w === "negative" || w === "a");
  let total = 0, cur = 0, seen = false, decimals = "", afterPoint = false;
  for (let i = 0; i < toks.length; i++) {
    const w = toks[i];
    if (afterPoint) { if (w in ONES && DIGIT_WORDS.has(w)) { decimals += ONES[w]; seen = true; } continue; }
    if (w === "point") { afterPoint = true; continue; }
    if (w in TENS) { cur += TENS[w]; seen = true; }
    else if (w in ONES) {
      // Homophones like "to" and "for" only count when the whole utterance is number words.
      if (!/^(to|too|for|won|ate|o|oh)$/.test(w) || only) { cur += ONES[w]; seen = true; }
    }
    else if (w === "hundred") { cur = (cur || 1) * 100; seen = true; }
    else if (w === "a" && /^(hundred|thousand|million)$/.test(toks[i + 1] ?? "")) cur += 1;
    else if (w in SCALES) { total += (cur || 1) * SCALES[w]; cur = 0; seen = true; }
  }
  if (!seen) return null;
  const whole = String(total + cur);
  return { kind: "number", value: (neg ? "-" : "") + whole + (decimals ? "." + decimals : "") };
}
